import {
  FormPageInputData,
  FormPageInputItem,
  SingleSelectionPropertyPulldown,
} from 'shared';
import { AssortedDrillDownProperties } from './assortDrillDownProperties';
import {
  isSingleSelectionPropertyPulldownWithDrillDown,
  isFormPageInputItemSingleSelectionPulldown,
} from './typeGuard';

const setDrillDownValidPairValue = (
  property: SingleSelectionPropertyPulldown,
  formPageInputData: FormPageInputData,
): FormPageInputItem => {
  const inputData = {
    ...formPageInputData,
  };
  if (!isSingleSelectionPropertyPulldownWithDrillDown(property)) {
    return inputData[property.propertyId];
  }
  const { drillDown, propertyId } = property;
  const parentInputItem = inputData[drillDown.parentId];
  const childInputItem = inputData[propertyId];

  if (
    !isFormPageInputItemSingleSelectionPulldown(parentInputItem) ||
    !isFormPageInputItemSingleSelectionPulldown(childInputItem)
  ) {
    return childInputItem;
  }

  const childValues = drillDown.valuePairs.find(
    (pair) => pair.parentValue === parentInputItem.value,
  )?.childValues;
  if (parentInputItem.value == null || childValues == null) {
    return {
      ...childInputItem,
      value: undefined,
    };
  }

  const includeUnselected = property.candidates.some(
    (candidate) => candidate.value == null,
  );
  const filteredChildValues = childValues.filter((v) =>
    property.properties.value.enum.includes(v),
  );

  const value = includeUnselected ? undefined : filteredChildValues[0];

  if (childInputItem.value == null) {
    return {
      ...childInputItem,
      value,
    };
  }

  const currentValueIsValidPair = filteredChildValues.includes(
    childInputItem.value,
  );

  if (!currentValueIsValidPair) {
    return {
      ...childInputItem,
      value,
    };
  }

  return childInputItem;
};

export const convertValuesForDrillDownFromAssortedPropertiesGroup = (
  assortedPropertyGroup: SingleSelectionPropertyPulldown[],
  formPageInputData: FormPageInputData,
): FormPageInputData => {
  return assortedPropertyGroup.reduce((acc, property) => {
    return {
      ...acc,
      [property.propertyId]: setDrillDownValidPairValue(property, acc),
    };
  }, formPageInputData);
};

type ConvertValuesForDrillDownPayload = {
  assortedDrillDownProperties: AssortedDrillDownProperties;
  formPageInputData: FormPageInputData;
};

export const convertValuesForDrillDown = ({
  assortedDrillDownProperties,
  formPageInputData,
}: ConvertValuesForDrillDownPayload): FormPageInputData => {
  if (!assortedDrillDownProperties.length) {
    return formPageInputData;
  }
  return assortedDrillDownProperties.reduce<FormPageInputData>(
    (acc, propertyGroup) => {
      return convertValuesForDrillDownFromAssortedPropertiesGroup(
        propertyGroup,
        acc,
      );
    },
    formPageInputData,
  );
};
